import React, { useState } from "react";
import { useParams } from "react-router-dom";
import Main from "./Main";
import { makeCompanyNav } from "./nav";
import { buyerSearch } from "../services/server";

/** Hitta köpare ? SNI-baserad företagssökning through FöretagsAPI. */
export default function BuyerSearch() {
  const { company } = useParams();
  const [sniCode, setSniCode] = useState("");
  const [city, setCity] = useState("");
  const [minRevenue, setMinRevenue] = useState("");
  const [maxRevenue, setMaxRevenue] = useState("");
  const [sort, setSort] = useState("revenue_desc");
  const [limit] = useState(5);
  const [offset, setOffset] = useState(0);

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  async function runSearch(useOffset = 0) {
    if (!sniCode.trim()) {
      setError({ kind: "validation", message: "SNI-kod krävs (5 siffror)." });
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const body = {
        sni_code: sniCode.trim(),
        limit: parseInt(limit, 10) || 5,
        offset: useOffset,
        sort,
      };
      if (city.trim()) body.city = city.trim();
      if (minRevenue) body.min_revenue = minRevenue;
      if (maxRevenue) body.max_revenue = maxRevenue;

      const data = await buyerSearch(body);
      setResults(data);
      setOffset(useOffset);
    } catch (err) {
      const detail = err?.body || err?.data || err;
      setError({
        kind: detail?.code === "threshold_error" ? "threshold" : detail?.code === "quota_error" ? "quota" : detail?.code === "validation_error" ? "validation" : "generic",
        message: detail?.detail || detail?.error || err?.message || "Sokningen misslyckades.",
        totalCount: detail?.totalCount,
      });
      setResults(null);
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    runSearch(0);
  }

  const companies = results?.companies || [];
  const metadata = results?.metadata || null;

  return (
    <Main pageTitle="Hitta köpare" navItems={company ? makeCompanyNav(company) : undefined}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: 24 }}>
        <header style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Hitta köpare</h1>
          <p style={{ fontSize: 14, color: "#6b7280", marginTop: 4 }}>
            Sök svenska företag efter SNI-kod, stad och omsättning.
          </p>
        </header>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
            <Field label="SNI-kod (5 siffror)" required>
              <input
                type="text"
                value={sniCode}
                onChange={(e) => setSniCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="t.ex. 70200"
                inputMode="numeric"
                style={inputStyle}
              />
            </Field>

            <Field label="Stad (valfritt)">
              <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="t.ex. Stockholm" style={inputStyle} />
            </Field>

            <Field label="Minsta omsättning (SEK)">
              <input type="number" min="0" value={minRevenue} onChange={(e) => setMinRevenue(e.target.value)} style={inputStyle} />
            </Field>

            <Field label="Största omsättning (SEK)">
              <input type="number" min="0" value={maxRevenue} onChange={(e) => setMaxRevenue(e.target.value)} style={inputStyle} />
            </Field>

            <Field label="Sortering">
              <select value={sort} onChange={(e) => setSort(e.target.value)} style={inputStyle}>
                <option value="revenue_desc">Omsättning (störst först)</option>
                <option value="revenue_asc">Omsättning (minst först)</option>
                <option value="name">Bokstavsordning</option>
              </select>
            </Field>

          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "10px 24px",
                fontSize: 14,
                fontWeight: 600,
                background: loading ? "#9ca3af" : "#1C1917",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: loading ? "wait" : "pointer",
              }}
            >
              {loading ? "Söker…" : "Sök"}
            </button>
          </div>
        </form>

        {error && (
          <div style={errorBoxStyle(error.kind)}>
            <strong>{
              error.kind === "validation" ? "Ogiltig inmatning" :
              "Fel"
            }</strong>
            <div style={{ marginTop: 4 }}>{error.message}</div>
            {error.totalCount && (
              <div style={{ marginTop: 4, fontSize: 12, color: "#9ca3af" }}>
                Totalt {error.totalCount.toLocaleString("sv-SE")} bolag matchar SNI-koden.
              </div>
            )}
          </div>
        )}

        {metadata && (
          <div style={metaBoxStyle}>
            <span><strong>{metadata.resultCount ?? companies.length}</strong> av <strong>{metadata.totalCount?.toLocaleString("sv-SE")}</strong> bolag</span>
            {metadata.sniDescription && <span>· {metadata.sniDescription}</span>}
            <span style={{ marginLeft: "auto", color: "#9ca3af", fontSize: 12 }}>
              FöretagsAPI | {metadata.creditsCharged || 0} krediter anvanda
            </span>
          </div>
        )}

        {companies.length > 0 && (
          <>
            <div style={tableWrapStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <Th>Bolag</Th>
                    <Th>Orgnr</Th>
                    <Th>Stad</Th>
                    <Th align="right">Omsättning</Th>
                    <Th align="right">Marginal</Th>
                    <Th align="right">Anställda</Th>
                    <Th>Verksamhet</Th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c.orgNumber || c.id} style={trStyle}>
                      <Td>
                        <div style={{ fontWeight: 600 }}>{c.name}</div>
                        {c.sniCodes?.sni1_name && (
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>{c.sniCodes.sni1_name}</div>
                        )}
                      </Td>
                      <Td>{c.orgNumber}</Td>
                      <Td>{c.postalAddress?.city || "—"}</Td>
                      <Td align="right">{c.financials?.revenueClass || fmtSek(c.financials?.revenue)}</Td>
                      <Td align="right">{fmtPct(c.financials?.operatingMargin)}</Td>
                      <Td align="right">{c.financials?.employees ?? "—"}</Td>
                      <Td>
                        <div style={{ fontSize: 12, maxWidth: 280, color: "#4b5563" }}>
                          {c.businessDescription || "—"}
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {metadata?.hasMore && (
              <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 16 }}>
                <button
                  type="button"
                  disabled={offset === 0}
                  onClick={() => runSearch(Math.max(0, offset - (parseInt(limit, 10) || 5)))}
                  style={paginationStyle(offset === 0)}
                >
                  ← Föregående
                </button>
                <span style={{ fontSize: 13, color: "#6b7280", alignSelf: "center" }}>
                  Sida {Math.floor(offset / (parseInt(limit, 10) || 5)) + 1}
                </span>
                <button
                  type="button"
                  onClick={() => runSearch(offset + (parseInt(limit, 10) || 5))}
                  style={paginationStyle(false)}
                >
                  Nästa →
                </button>
              </div>
            )}
          </>
        )}

        {results && companies.length === 0 && !error && (
          <div style={{ padding: 32, textAlign: "center", color: "#6b7280" }}>
            Inga bolag matchade sökkriterierna.
          </div>
        )}
      </div>
    </Main>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function Field({ label, required, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
        {label}{required && <span style={{ color: "#dc2626" }}> *</span>}
      </span>
      {children}
    </label>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th style={{
      textAlign: align, fontSize: 11, fontWeight: 600, textTransform: "uppercase",
      color: "#6b7280", padding: "10px 12px", borderBottom: "1px solid #e5e7eb",
      letterSpacing: 0.4, background: "#fafaf9",
    }}>{children}</th>
  );
}

function Td({ children, align = "left" }) {
  return (
    <td style={{
      textAlign: align, fontSize: 13, color: "#111827",
      padding: "10px 12px", borderBottom: "1px solid #f3f4f6", verticalAlign: "top",
    }}>{children}</td>
  );
}

function fmtSek(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} MSEK`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} kSEK`;
  return `${n} SEK`;
}

function fmtPct(n) {
  if (n == null) return "—";
  return `${(n * 100).toFixed(1)} %`;
}

// ── Styles ────────────────────────────────────────────────────────────

const formStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
};

const inputStyle = {
  padding: "8px 12px",
  fontSize: 13,
  border: "1px solid #d1d5db",
  borderRadius: 6,
  background: "#fff",
  width: "100%",
  boxSizing: "border-box",
};

const errorBoxStyle = (kind) => ({
  padding: "12px 16px",
  marginBottom: 16,
  background: kind === "threshold" ? "#FFFBEB" : kind === "quota" ? "#FEE2E2" : "#FEF2F2",
  border: `1px solid ${kind === "threshold" ? "#FBBF24" : "#FCA5A5"}`,
  borderRadius: 8,
  fontSize: 13,
  color: "#1C1917",
});

const metaBoxStyle = {
  display: "flex",
  gap: 12,
  alignItems: "center",
  padding: "10px 14px",
  background: "#F5F5F4",
  border: "1px solid #E7E5E4",
  borderRadius: 8,
  marginBottom: 12,
  fontSize: 13,
  color: "#374151",
};

const tableWrapStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  overflow: "hidden",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
};

const trStyle = {
  transition: "background 0.1s",
};

const paginationStyle = (disabled) => ({
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  background: disabled ? "#f3f4f6" : "#fff",
  color: disabled ? "#9ca3af" : "#374151",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  cursor: disabled ? "not-allowed" : "pointer",
});
